import { onDestroy, onMount } from 'svelte';

interface appearParameters {
	once: boolean;
	trigger: string;
	element?: HTMLElement | null;
}

export function useAppear(parameters: appearParameters) {
	/**
	 * Parameters
	 */
	const { once = true, trigger = '20%' } = parameters;

	/**
	 * Reactives
	 */
	let element = $state(null);
	let isVisible = $state(false);

	/**
	 * Variables
	 */
	let intersectionObserver: IntersectionObserver | null = null;
	let triggered = false;

	/**
	 * Callbacks
	 */

	/**
	 * Callback function invoked when intersection occurs.
	 * @param {IntersectionObserverEntry[]} _entries - An array of IntersectionObserverEntry objects.
	 */
	const onIntersect = (_entries: IntersectionObserverEntry[]) => {
		let isIntersecting;

		// Loop through each entry to determine if any of them are intersecting.
		_entries.forEach((entry) => {
			isIntersecting = entry.isIntersecting;
		});

		// If any entry is intersecting
		if (isIntersecting) {
			// If 'once' is true and the trigger has already been triggered, destroy.
			if (once && triggered) {
				destroy();
				return;
			}

			triggered = true;
			isVisible = true;
		} else {
			if (once) return;

			if (!triggered) return;

			isVisible = false;
		}
	};

	/**
	 * Initialize the composable.
	 */
	const init = () => {
		intersectionObserver = new IntersectionObserver(onIntersect, { rootMargin: `0% 0% -${trigger} 0%`, threshold: 0 });

		const el = parameters.element || element;

		if (el) intersectionObserver.observe(el);
	};

	/**
	 * Destroy the component.
	 */
	const destroy = () => {
		// Stop the intersection observer if it's active.
		intersectionObserver?.disconnect();
		intersectionObserver = null;
	};
	// When the component is mounted, set up the Intersection Observer
	onMount(init);

	// When the component is unmounted, stop the Intersection Observer
	onDestroy(destroy);

	// Return the element reference for use in the component
	return {
		set element(value) {
			element = value;
		},
		get element() {
			return element;
		},
		get isVisible() {
			return isVisible;
		},
	};
}
